import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type { Inquiry } from '../types';
import { useInquiries } from '../hooks/useInquiries';
import { useToken, useWebViewLoading } from '../../../shared/hooks';
import { isWebViewEnvironment, postMessageToChannel, postMessageToFlutter } from '../../../shared/services/bridge';

import './InquiryList.css';

// ステータスの表示名を取得（ダッシュボードと統一）
const getDisplayStatus = (status: Inquiry['status']): string => {
    switch (status) {
        case '訪問確定済':
            return '訪問日確定';
        case '調査中':
            return '訪問作業中';
        case '調査見積もり済':
            return '注文手続き';
        case '見積もりサイン済':
            return '注文済み';
        case '作業報告書確認':
            return '納品手続き';
        case '受付済':
            return '受付中';
        case '支払い待ち':
            return '支払手続き';
        case '調査のみ完了':
            return '調査のみ完了';
        case '作業完了':
            return '作業完了';
        default:
            return status;
    }
};

/**
 * 日付文字列から日付部分と時間部分を分離して返す
 * APIからの形式（yyyy-MM-dd HH:mm:ss）を { date: 'yyyy/MM/dd', time: 'HH:mm' } に変換
 */
const parseDateParts = (dateStr: string): { date: string; time: string } => {
    if (!dateStr || dateStr === '日程調整中') return { date: dateStr, time: '' };
    // スペースで日付と時刻を分割（yyyy/MM/dd HH:mm:ss or yyyy-MM-dd HH:mm:ss）
    const parts = dateStr.split(' ');
    const datePart = parts[0].replace(/-/g, '/');
    const timePart = parts[1] || '';
    if (timePart) {
        const timeShort = timePart.split(':').slice(0, 2).join(':');
        return { date: datePart, time: timeShort };
    }
    return { date: datePart, time: '' };
};

/**
 * 日付・時間を2行表示するセル（折り返し禁止、狭い場合は短縮フォーマット）
 */
const DateTimeCell: React.FC<{ dateStr: string }> = ({ dateStr }) => {
    const cellRef = useRef<HTMLDivElement>(null);
    const [isNarrow, setIsNarrow] = useState(false);

    useEffect(() => {
        if (!cellRef.current) return;
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                // Flutter側と同じ閾値: 80px未満でMM/dd短縮
                setIsNarrow(entry.contentRect.width < 80);
            }
        });
        observer.observe(cellRef.current);
        return () => observer.disconnect();
    }, []);

    const parts = parseDateParts(dateStr);
    // ダッシュボードと統一: yyyy/MM/dd（1行目）+ HH:mm（2行目）の2行表示
    // 狭い場合はMM/ddにフォールバック（レスポンシブ対応）
    const displayDate = isNarrow && parts.date.length > 5
        ? parts.date.slice(5)
        : parts.date;

    return (
        <div ref={cellRef} className="date-time-cell">
            <div className="date-line">{displayDate}</div>
            {parts.time && <div className="time-line">{parts.time}</div>}
        </div>
    );
};

// コンポーネントのPropsの型を定義
interface InquiryRowProps {
    inquiry: Inquiry;
}

// 一覧の行をレンダリングするヘルパーコンポーネント
const InquiryRow: React.FC<InquiryRowProps> = ({ inquiry }) => {
    const navigate = useNavigate();

    const handleRowClick = () => {
        if (isWebViewEnvironment()) {
            // ローディング表示を先に開始（React側で制御）
            postMessageToFlutter('showLoading');
            // webview_flutterのJavaScriptChannel経由でFlutterに通知
            postMessageToChannel('InquiryHandler', inquiry.id.toString());
        } else {
            navigate(`/inquiries/${inquiry.id}`);
        }
    };

    return (
        <div className="inquiry-row" onClick={handleRowClick}>
            <div className="cell status">{getDisplayStatus(inquiry.status)}</div>
            <div className="cell request-date">
                <DateTimeCell dateStr={inquiry.requestDate} />
            </div>
            <div className="cell location">{inquiry.equipmentName || ''}</div>
            <div className="cell visit-date">
                <DateTimeCell dateStr={inquiry.visitDate} />
            </div>
        </div>
    );
};

function InquiryList() {
    const [searchParams] = useSearchParams();
    const homeId = searchParams.get('homeId') || undefined;
    const token = useToken();

    const { inquiries, isLoading, isLoadingMore, hasMore, loadMore, error, refetch } = useInquiries(homeId, token);

    // 無限スクロール用: 最下部の監視要素
    const observerRef = useRef<IntersectionObserver | null>(null);
    const sentinelRef = useCallback((node: HTMLDivElement | null) => {
        if (observerRef.current) observerRef.current.disconnect();
        if (!node) return;

        observerRef.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
                loadMore();
            }
        }, { threshold: 0.1 });

        observerRef.current.observe(node);
    }, [hasMore, isLoadingMore, loadMore]);

    useEffect(() => {
        document.body.style.overflow = 'auto';
    }, []);

    // Flutter WebViewのローディング制御（データ取得完了時 & ページ再表示時）
    const contentRef = useWebViewLoading(isLoading);

    // ローディング中はFlutter側のオーバーレイに任せるため、空のコンテナを返す
    if (isLoading) {
        return <div className="inquiry-container" />;
    }

    // エラー時: メッセージ + 再読込ボタンを表示
    // （過去バグ: エラー時に空画面 + hasMore=true の組み合わせで IntersectionObserver が
    //   無限ループし、API が連打されていた。現在は hasMore=false になりループは起きないが、
    //   ユーザーにエラーを伝える UI を提供する）
    if (error) {
        return (
            <main ref={contentRef} className="inquiry-container">
                <h1 className="main-title">修繕依頼一覧</h1>
                <hr className="divider" />
                <div className="inquiry-error">
                    <p className="inquiry-error-message">お問い合わせ一覧を取得できませんでした。</p>
                    <button
                        type="button"
                        className="inquiry-error-retry"
                        onClick={() => refetch()}
                    >
                        再読込
                    </button>
                </div>
            </main>
        );
    }

    return (
        <main ref={contentRef} className="inquiry-container">
            <h1 className="main-title">修繕依頼一覧</h1>
            <hr className="divider" />

            <div className="inquiry-table">
                <div className="inquiry-header">
                    <div className="cell status">受付状況</div>
                    <div className="cell request-date">受付日時</div>
                    <div className="cell location">依頼場所</div>
                    <div className="cell visit-date">訪問日時</div>
                </div>
                {inquiries.map(item => <InquiryRow key={item.id} inquiry={item} />)}
            </div>

            {/* 無限スクロール: 監視要素 */}
            {hasMore && (
                <div ref={sentinelRef} className="load-more-sentinel">
                    {isLoadingMore && <div className="loading-more">読み込み中...</div>}
                </div>
            )}
        </main>
    );
}

export default InquiryList;
